<template>
  <Layout>
    <template #header-container>
      <div class="mc-list-header">
        <PageHeader isSticky :title="title">
          <template #afterTitle>
            <div class="d-flex gap-2">
              <McButtonBack v-if="$route.params.id === undefined" href="/setting/webhook" />
              <McButtonBack v-else :href="`/setting/webhook/detail/${$route.params.id}`" />
              <McButton
                v-if="currentStatus != 'config'"
                variant="secondary"
                :text="$t('global.prev')"
                icon="icon-outline_arrow-left"
                iconPosition="left"
                @click="prevWizard()"
              />
              <McButton
                v-if="currentStatus != 'fleet'"
                variant="primary"
                :text="$t('global.next')"
                icon="icon-outline_arrow-right"
                iconPosition="right"
                @click="nextWizard()"
              />
              <McButton
                v-if="currentStatus == 'fleet'"
                variant="primary"
                :text="$t('global.save')"
                icon="icon-outline_save"
                :disabled="isSaving"
                @click="$route.params.id !== undefined ? updateData() : saveData()"
              />
            </div>
          </template>

          <template #footer>
            <b-skeleton-wrapper :loading="isLoading">
              <template #loading>
                <b-skeleton-img no-aspect height="72px" class="mc-wizard-skeleton" />
              </template>
              <div class="custom-mc-wizard">
                <McWizard
                  :items="wizardItems"
                  :activeKey="currentStatus"
                  :withNumber="true"
                  class="mc-wizard"
                />
              </div>
            </b-skeleton-wrapper>
          </template>
        </PageHeader>
      </div>
    </template>

    <div class="mc-webhook-form">
      <FormulateForm ref="formRef" v-model="formValues" class="mc-form" :errors="formErrors">
        <b-row v-if="currentStatus == 'config'" class="g-0">
          <b-card class="mc-webhook-info__card">
            <b-row class="g-0">
              <div class="mc-webhook-info__input-group">
                <div>
                  <b-form-group
                    id="url"
                    :label="$t('setting.list.webhook.url')"
                    label-for="url"
                  >
                    <b-form-input
                      id="url"
                      v-model="url"
                      :placeholder="$t('global.form.enter') + ' ' + $t('setting.list.webhook.url')"
                      :class="{
                        'is-invalid': (isEmptyUrl && urlTouched) || (isInvalidUrl && urlTouched),
                      }"
                      trim
                      @blur="urlTouched = true"
                    />
                    <div
                      v-if="!(urlTouched && (isEmptyUrl || isInvalidUrl))"
                      class="custom-input-help-text input-md"
                    >
                      <label class="custom-required-help-text">
                        <span>* </span>{{ $t('global.validation.required') }}
                      </label>
                    </div>
                    <div class="invalid-feedback">
                      <span v-if="urlTouched && isEmptyUrl">
                        {{ $t('setting.list.webhook.url') }}
                        {{ $t('global.validation.required') }}
                      </span>
                      <span v-else-if="urlTouched && isInvalidUrl">
                        {{ $t('setting.list.webhook.validation.urlInvalid') }}
                      </span>
                    </div>
                  </b-form-group>
                </div>
              </div>
            </b-row>
          </b-card>
          <b-row class="g-0">
            <div class="wall">
              <template v-for="category in eventCategories">
                <div v-if="eventLists[category].length > 0" :key="category" class="item">
                  <b-row class="g-0">
                    <b-col md="6">
                      <h5 class="card-title">
                        {{ $t(`global.alert.type.${category}`) }}
                      </h5>
                    </b-col>
                    <b-col md="6" align-self="end">
                      <div class="float-end">
                        <h6
                          v-if="!isCheckAll[category]"
                          class="text-primary pointer"
                          @click="handleCheckAllEvent(true, category)"
                        >
                          {{ $t('global.action.activateAll') }}
                        </h6>
                        <h6
                          v-else
                          class="text-primary pointer"
                          @click="handleCheckAllEvent(false, category)"
                        >
                          {{ $t('global.action.nonactivateAll') }}
                        </h6>
                      </div>
                    </b-col>
                    <b-card>
                      <div v-for="(item, index) in eventLists[category]" :key="index">
                        <b-row class="align-items-center mt-2">
                          <b-col md="1" class="d-flex justify-content-end">
                            <div class="form-check">
                              <input
                                :id="`checkbox-${category}-${item.id}`"
                                v-model="item.checked"
                                class="form-check-input"
                                type="checkbox"
                                @click="handleCheckEvent($event, item, category)"
                              />
                            </div>
                          </b-col>
                          <b-col md="11">
                            <label class="mb-0" :for="`checkbox-${category}-${item.id}`">
                              {{ item.title() }}
                            </label>
                          </b-col>
                        </b-row>
                      </div>
                    </b-card>
                  </b-row>
                </div>
              </template>
            </div>
          </b-row>
        </b-row>

        <b-row v-if="currentStatus == 'fleet'" class="g-0">
          <McGrapqlTable
            ref="fleetTableRef"
            :apiClient="vehicle"
            :fields="fields"
            :customHandlers="customHandlers"
            :searchable="true"
            :withCard="true"
            trackBy="id"
            hasCheckbox
            :activeFilters="activeFilters"
            :hasFilter="true"
            :hasDelete="false"
            :hasMassSelect="true"
            :initialSelectedIds="selectedVehicleIds"
            :refreshData="isLoading"
            @handleSelectValue="handleCheckVehicle"
          >
            <template #button-filter="{ isSelected, selectedIds }">
              <div class="mt-2 d-flex justify-content-between">
                <McAlert withIcon variant="info">
                  <template #label>
                    <span>
                      Pemilihan kendaraan di sini tidak memengaruhi perubahan data
                      <strong>Penugasan</strong> dan <strong>Quick Assignment</strong>
                    </span>
                  </template>
                </McAlert>
                <ModalFilter
                  :activeFilters="activeFilters"
                  :isSelected="isSelected"
                  :ids="selectedIds"
                  @handleChangeActiveFilters="handleChangeActiveFilters"
                  @refreshData="handleRefreshData"
                />
              </div>
            </template>
            <template #cell(status)="data">
              <span v-if="data.item.status === 'I'" class="mc-status mc-status--green">
                {{ $t('global.active') }}
              </span>
              <span v-else-if="data.item.status === 'R'" class="mc-status mc-status--blue">
                {{ $t('global.repair') }}
              </span>
              <span v-else-if="data.item.status === 'A'" class="mc-status mc-status--red">
                {{ $t('global.archive') }}
              </span>
            </template>
          </McGrapqlTable>
        </b-row>
      </FormulateForm>
    </div>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2.vue'
import Layout from '@/router/layouts/main'
import { mapGetters } from 'vuex'
import McButton from '@/components/McButton'
import McButtonBack from '@/components/McButtonBack'
import McWizard from '@/components/McWizard'
import McAlert from '@/components/McAlert'
import McGrapqlTable from './components/table'
import ModalFilter from './ModalFilter'
import {
  listGeneral,
  listDMS,
  listDriverBehavior,
  listADAS,
  listDataUpdate,
  listFleetTask,
  listQuickAssignment,
  getTypeNames,
} from './lib/listAlert'
import { GET_VEHICLE_FILTER } from '@/services/api/master/fleet'
import { GET_VEHICLE_TYPE } from '@/services/api/setting/fleet/fleetType'
import * as webhookEventAPI from '@/services/rest-api-webhook/lib/event/event'
import * as webhookAPI from '@/services/rest-api-webhook/lib/webhook/webhook'
import { isEqual } from 'lodash'
import { Helper } from '@/mixin/helper'

/**
 * Starter component
 */
export default {
  page: {
    title: 'Setting',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McButtonBack,
    McWizard,
    McAlert,
    McGrapqlTable,
    ModalFilter,
  },
  mixins: [Helper],
  data() {
    return {
      customHandlers: {
        handleClickRow: (item) => {
          return this.onRowClicked(item)
        },
      },
      isLoading: false,
      currentStatus: 'config',
      formValues: {},
      formErrors: {},
      url: null,
      isResultSuccessful: false,
      isSaving: false,
      selectedAll: false,
      activeFilters: {},
      fleetTypeList: [],
      selectedVehicleIds: [],
      selectedEventIds: [],
      listEventGroups: [],
      oldWebhookForm: {
        url: '',
        event_ids: [],
        vehicle_ids: [],
      },

      eventLists: {
        general: [],
        dataUpdate: [],
        dms: [],
        driverBehavior: [],
        adas: [],
        fleetTask: [],
        quickAssignment: [],
      },

      isCheckAll: {
        general: false,
        dataUpdate: false,
        dms: false,
        driverBehavior: false,
        adas: false,
        fleetTask: false,
        quickAssignment: false,
      },

      eventCategories: [
        'general',
        'dataUpdate',
        'dms',
        'driverBehavior',
        'adas',
        'fleetTask',
        'quickAssignment',
      ],
      eventMap: {
        general: {
          group: 'General',
          eventListVariableName: 'general',
          func: listGeneral,
        },
        dataUpdate: {
          group: 'Data Update',
          eventListVariableName: 'dataUpdate',
          func: listDataUpdate,
        },
        dms: {
          group: 'DMS',
          eventListVariableName: 'dms',
          func: listDMS,
        },
        driverBehavior: {
          group: 'Driver Behavior',
          eventListVariableName: 'driverBehavior',
          func: listDriverBehavior,
        },
        adas: {
          group: 'ADAS',
          eventListVariableName: 'adas',
          func: listADAS,
        },
        fleetTask: {
          group: 'Data Update',
          eventListVariableName: 'fleetTask',
          func: listFleetTask,
        },
        quickAssignment: {
          group: 'Data Update',
          eventListVariableName: 'quickAssignment',
          func: listQuickAssignment,
        },
      },
      isEmptyUrl: false,
      isInvalidUrl: false,
      urlTouched: false,
    }
  },
  computed: {
    title() {
      const action = this.$route.params.id === undefined ? 'create' : 'change'
      return this.$t('global.action.' + action) + ' ' + this.$t('setting.homePage.webhook')
    },
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
    }),
    wizardItems() {
      return [
        { key: 'config', label: this.$t('setting.list.webhook.alertConfig') },
        { key: 'fleet', label: this.$t('setting.list.webhook.selectFleet') },
      ]
    },
    vehicle() {
      return {
        get: async (filter) => {
          const res = await this.$apollo.query({
            query: GET_VEHICLE_FILTER,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                ...filter,
                ...this.activeFilters,
              },
            },
          })

          return res.data?.vehiclesFilter?.vehiclesFilterPaginated || []
        },
      }
    },
    fields() {
      return [
        {
          key: 'licensePlate',
          label: this.$t('global.object.licensePlate'),
          isSortable: false,
        },
        {
          key: 'vehicleType.type',
          label: this.$t('global.field.vehicleType'),
          isSortable: false,
        },
        {
          key: 'status',
          label: 'Status',
          isSortable: false,
        },
      ]
    },
    isValidUrl() {
      return this.validateUrl(this.url)
    },
  },
  mounted() {
    this.getEventList()
    this.getFleetType()
  },
  methods: {
    async getEventList() {
      const response = await webhookEventAPI.get()
      this.listEventGroups = response.data

      Object.entries(this.eventMap).forEach(([_, value]) => {
        const { group, eventListVariableName, func } = value
        const listEvent = getTypeNames(this.listEventGroups, group)

        const newList = func
          .call(this)
          .filter((item) => item.code in listEvent)
          .map((item) => ({
            ...item,
            id: listEvent[item.code].event_id,
            checked: false,
          }))

        this.$set(this.eventLists, eventListVariableName, [...newList])
      })

      if (this.$route.params.id) {
        await this.getDetailEvent(this.$route.params.id)
      }
    },
    async getDetailEvent(id) {
      const { data } = await webhookAPI.getDetail(id)
      const { url, vehicle, event } = data

      this.url = url
      this.selectedVehicleIds = vehicle?.ids || []
      this.selectedEventIds = event?.ids || []
      this.oldWebhookForm = { url, event_ids: event?.ids, vehicle_ids: vehicle?.ids }

      // Fill in the form with the old data
      Object.values(this.eventMap).forEach(({ eventListVariableName }) => {
        this.eventLists[eventListVariableName]?.forEach((item) => {
          item.checked = this.selectedEventIds.includes(item.id)
        })

        // Determine checkAll state
        this.isCheckAll[eventListVariableName] = this.eventLists[eventListVariableName].every(
          (item) => this.selectedEventIds.includes(item.id)
        )
      })
    },
    async getFleetType() {
      const res = await this.$apollo.query({
        query: GET_VEHICLE_TYPE,
        fetchPolicy: 'no-cache',
        variables: {},
      })

      this.fleetTypeList = res.data.vehicleTypes.vehicleTypes.data
      return this.fleetTypeList
    },
    handleChangeActiveFilters(filters) {
      filters.forEach((filter) => {
        let filterKey = filter.key
        let filterValues = filter.values
        if (filter.key === 'vehicleTypeId') {
          filterKey = 'vehicleType'
          if (Array.isArray(filter.values)) {
            filterValues = this.fleetTypeList
              .filter((item) => filter.values.includes(item.vehicleTypeId.toString()))
              .map((item) => `${item.manufacturer}/${item.model}/${item.type}`)
          }
        }
        this.$delete(this.activeFilters, filterKey)
        if (
          filterValues === null ||
          filterValues === undefined ||
          filterValues === '' ||
          filterValues === false
        )
          return

        if (Array.isArray(filterKey) && filterKey.length === 0) return

        this.$set(this.activeFilters, filterKey, filterValues)
      })
    },
    handleRefreshData(value) {
      this.isLoading = value
      setTimeout(() => {
        this.isLoading = !value
      }, 300)
    },
    nextWizard() {
      if (this.currentStatus == 'config') {
        if (!this.url) {
          this.urlTouched = true
          this.isEmptyUrl = true
          return
        }

        if (!this.isValidUrl) {
          this.urlTouched = true
          this.isInvalidUrl = true
          this.isEmptyUrl = false
          return
        }

        this.urlTouched = false
        this.isEmptyUrl = false
        this.isInvalidUrl = false
        this.currentStatus = 'fleet'
      }
    },
    prevWizard() {
      if (this.currentStatus == 'fleet') {
        this.currentStatus = 'config'
      }
    },
    handleCheckVehicle(vehicles) {
      if (Object.entries(vehicles).length === 0) {
        this.selectedVehicleIds = []
        return
      }
      Object.entries(vehicles).forEach(([key, value]) => {
        if (value === true) {
          this.selectedVehicleIds = [...new Set([...this.selectedVehicleIds, parseInt(key)])]
        } else {
          this.selectedVehicleIds = this.selectedVehicleIds.filter((vehicleId) => {
            return vehicleId !== parseInt(key)
          })
        }
      })
    },
    handleCheckEvent(event, data, groupName) {
      const { checked } = event.target
      const { id } = data
      if (checked) {
        this.selectedEventIds = [...new Set([...this.selectedEventIds, id])]
      } else {
        this.selectedEventIds = this.selectedEventIds.filter((eventId) => {
          return eventId !== id
        })
      }

      // Update the isCheckAll state for this groupName
      if (this.eventMap[groupName]) {
        const { eventListVariableName } = this.eventMap[groupName]

        this.isCheckAll[eventListVariableName] = this.eventLists[eventListVariableName].every(
          (item) => this.selectedEventIds.includes(item.id)
        )
      }
    },
    handleCheckAllEvent(checked, groupName) {
      // Make sure groupName is included in eventMap
      if (this.eventMap[groupName]) {
        const { eventListVariableName } = this.eventMap[groupName]

        const idsToUpdate = this.eventLists[eventListVariableName].map((item) => item.id)

        if (checked) {
          this.selectedEventIds = [...new Set([...this.selectedEventIds, ...idsToUpdate])]
        } else {
          this.selectedEventIds = this.selectedEventIds.filter((id) => !idsToUpdate.includes(id))
        }

        // Update individual item checked status
        this.eventLists[eventListVariableName].forEach((item) => {
          this.$set(item, 'checked', checked)
        })

        // Update the isCheckAll state for this groupName
        this.isCheckAll[eventListVariableName] = checked
      }
    },
    validateForm(form) {
      const validationRules = [
        { field: 'url', message: 'setting.list.webhook.validation.urlRequired' },
        { field: 'event_ids', message: 'setting.list.webhook.validation.eventRequired' },
      ]
      
      // List of event ids that are not required to select vehicle ids
      const eventIds = [15, 16, 17, 29, 30, 31, 32, 33]

      // if there is event except in eventIds above, then vehicle_ids is required
      if (this.selectedEventIds.find(item => !eventIds.includes(item)) !== undefined) {
        validationRules.push({ field: 'vehicle_ids', message: 'setting.list.webhook.validation.vehicleRequired' })
      }

      for (const rule of validationRules) {
        if (
          Object.prototype.hasOwnProperty.call(form, rule.field) &&
          (!form[rule.field] || form[rule.field].length === 0)
        ) {
          this.$mcToast.error(this.$t(rule.message))
          return false
        }
      }

      return true
    },
    saveData() {
      this.$set(this, 'isSaving', true)
      const form = {}
      form.url = this.url
      form.event_ids = this.selectedEventIds
      form.vehicle_ids = this.selectedVehicleIds

      if (!this.validateForm(form)) {
        this.$set(this, 'isSaving', false)
        return
      }

      webhookAPI.store(form).then((response) => {
        if (!response.errors) {
          this.$mcToast.success(this.$t('global.alert.text.added'))
          this.$router.push({ name: 'Setting Webhook' })
        } else {
          this.$mcToast.error(response.message)
        }
        this.$set(this, 'isSaving', false)
      })
    },
    updateData() {
      this.$set(this, 'isSaving', true)
      const webhookId = this.$route.params.id
      const form = {}

      if (this.oldWebhookForm.url !== this.url) {
        form.url = this.url
      }
      if (!isEqual(this.selectedEventIds.sort(), this.oldWebhookForm.event_ids.sort())) {
        form.event_ids = this.selectedEventIds
      }
      if (!isEqual(this.selectedVehicleIds.sort(), this.oldWebhookForm.vehicle_ids.sort())) {
        form.vehicle_ids = this.selectedVehicleIds
      }

      if (Object.keys(form).length === 0) {
        this.$mcToast.info(this.$t('global.alert.text.noChanges'))
        this.$set(this, 'isSaving', false)
        return
      }
      if (!this.validateForm(form)) {
        this.$set(this, 'isSaving', false)
        return
      }

      webhookAPI.patch(webhookId, form).then((response) => {
        if (!response.errors) {
          this.$mcToast.success(this.$t('global.alert.text.successUpdate'))
          this.$router.push({ name: 'Setting Webhook' })
        } else {
          this.$mcToast.error(response.message)
        }
        this.$set(this, 'isSaving', false)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.wall {
  column-count: 2;
  column-gap: 18px;
}
.item {
  display: inline-block;
  width: 100%;
  margin-bottom: 10px;
  box-sizing: border-box;
}
</style>
